/**
 * Priority-driven Main-tab layout for H&T, Door/Window, and Motion sensors.
 * npm test src/utest/sidepanel/sensor_priority_layout.test.ts
 */

export const SENSOR_UI_OFFLINE = 0;
export const SENSOR_LOW_BATTERY_PERCENT = 20;
export const SENSOR_CRITICAL_BATTERY_PERCENT = 10;
export const SENSOR_NO_REPORTS_MS = 86_400_000;
export const SENSOR_TEMP_COLD_C = 18;
export const SENSOR_TEMP_COLD_PROMOTE_C = 16;
export const SENSOR_TEMP_WARM_C = 24;
export const SENSOR_TEMP_WARM_PROMOTE_C = 26;
export const SENSOR_CLIMATE_COOL = 'var(--color-theme-climate-cool)';
export const SENSOR_CLIMATE_WARM = 'var(--color-theme-climate-warm)';
export const SENSOR_CLIMATE_HUMIDITY = 'var(--color-theme-climate-humidity)';
export const SENSOR_CLIMATE_NEUTRAL = 'var(--color-theme-fg-02)';
/** Matches DS `.property-card.priority-primary` min-height. */
export const SENSOR_PRIORITY_PRIMARY_MIN_HEIGHT_PX = 158;
/** First-card top sits this many card heights above the content-pane center. */
export const SENSOR_PRIORITY_LEAD_OFFSET_RATIO = 1.5;
const SENSOR_INVALID_READING = new Set([-999, 999]);

const SENSOR_PRIORITY_PANE_SELECTOR = '.scroll-sidepanel, .sidepanel-content-body';

/**
 * Padding above the PriorityGrid so the top of the first card is 1.5x that
 * card's height above the vertical center of the Main-tab content pane.
 */
export function sensorPriorityLeadTopOffset(paneHeight: number, leadCardHeight: number): number {
	if (paneHeight <= 0 || leadCardHeight <= 0) return 0;
	return Math.max(0, paneHeight / 2 - leadCardHeight * SENSOR_PRIORITY_LEAD_OFFSET_RATIO);
}

export function sensorPriorityLeadCard(root: ParentNode | null | undefined): HTMLElement | undefined {
	const card = root?.querySelector('.property-card');
	return card instanceof HTMLElement ? card : undefined;
}

/** Visible Main-tab height (scrollport), not the view if last-events made it taller. */
export function sensorPriorityPaneHeight(view: HTMLElement | null | undefined): number {
	if (!view) return 0;
	const pane = view.closest(SENSOR_PRIORITY_PANE_SELECTOR);
	const el = pane instanceof HTMLElement ? pane : view;
	return el.clientHeight;
}

export type SensorPriorityFamily = 'ht' | 'dw' | 'motion';

export type SensorOverlay = 'offline' | 'noReports' | 'noGateway';

export type SensorPriorityCardPriority = 'primary' | 'wide' | 'secondary' | 'tertiary' | 'compact';

export type SensorPriorityCardTone = 'warning' | 'danger' | 'success' | 'muted' | 'info';

export type SensorPriorityLabel = {
	key: string;
	default: string;
};

export type SensorPriorityCard = {
	id: string;
	value: string;
	valueLabel?: SensorPriorityLabel;
	unit?: string;
	numericValue?: number;
	label: SensorPriorityLabel;
	icon: string;
	priority: SensorPriorityCardPriority;
	tone?: SensorPriorityCardTone;
	stale?: boolean;
	disabled?: boolean;
	kind?: 'metric' | 'status';
	accentColor?: string;
	span?: 'full';
};

export type SensorPriorityLayout = {
	family: SensorPriorityFamily;
	overlay: SensorOverlay | null;
	cards: SensorPriorityCard[];
};

export type SensorPriorityDevForce =
	| 'auto'
	| 'comfortable'
	| 'cold'
	| 'warm'
	| 'humid'
	| 'closed'
	| 'open'
	| 'tilted'
	| 'vibration'
	| 'clear'
	| 'detected'
	| 'dark'
	| 'lowBattery'
	| 'noReports'
	| 'noGateway'
	| 'offline';

export type SensorPriorityDevOption = {
	id: SensorPriorityDevForce;
	label: string;
};

export type SensorPriorityLayoutInput = {
	family: SensorPriorityFamily;
	deviceType: string;
	channel?: number;
	status?: Record<string, unknown>;
	settings?: Record<string, unknown>;
	temperatureUnit?: string;
	isBle: boolean;
	online: boolean;
	uiStateType?: number;
	hasReporter: boolean;
	noRecentReports: boolean;
	force?: SensorPriorityDevForce;
};

type SensorReading = {
	id: string;
	value: string;
	valueLabel?: SensorPriorityLabel;
	unit?: string;
	numericValue?: number;
	label: SensorPriorityLabel;
	icon: string;
	kind?: 'metric' | 'status';
	tone?: SensorPriorityCardTone;
	promote?: boolean;
	accentColor?: string;
};

const HT_TYPES = [
	'SHHT-1',
	'SNSN-0013A',
	'S3SN-0U12A',
	'S3SN-1U12A',
	'SBHT-003C',
	'SBHT-103C',
	'SBHT-203C',
] as const;
const DW_TYPES = ['SHDW-1', 'SHDW-2', 'SBDW-002C', 'SBDW-103C', 'SBDW-203C', 'SBDW-302C'] as const;
const MOTION_TYPES = ['SHMOS-01', 'SHMOS-02', 'SBMO-003Z', 'SBMO-103Z'] as const;

const HT_TYPE_SET = new Set<string>(HT_TYPES);
const DW_TYPE_SET = new Set<string>(DW_TYPES);
const MOTION_TYPE_SET = new Set<string>(MOTION_TYPES);

export const SENSOR_OVERLAY_COPY: Record<
	SensorOverlay,
	{ value: SensorPriorityLabel; label: SensorPriorityLabel; icon: string }
> = {
	offline: {
		value: { key: 'label_sensor_overlay_offline', default: 'Offline' },
		label: {
			key: 'label_sensor_overlay_offline_hint',
			default: 'Device unavailable',
		},
		icon: 'fa-solid fa-cloud-slash',
	},
	noReports: {
		value: {
			key: 'label_sensor_overlay_no_reports',
			default: 'No reports',
		},
		label: {
			key: 'label_sensor_overlay_no_reports_hint',
			default: 'No recent reports',
		},
		icon: 'fa-solid fa-flag',
	},
	noGateway: {
		value: {
			key: 'label_sensor_overlay_no_gateway',
			default: 'No gateway',
		},
		label: {
			key: 'label_sensor_overlay_no_gateway_hint',
			default: 'No active gateway',
		},
		icon: 'fa-solid fa-link-slash',
	},
};

export const getSensorPriorityFamily = (deviceType: string | undefined): SensorPriorityFamily | null => {
	if (!deviceType) return null;
	if (HT_TYPE_SET.has(deviceType)) return 'ht';
	if (DW_TYPE_SET.has(deviceType)) return 'dw';
	if (MOTION_TYPE_SET.has(deviceType)) return 'motion';
	return null;
};

export const sensorPriorityFlipKey = (layout: SensorPriorityLayout): string =>
	[
		layout.family,
		layout.overlay ?? '',
		...layout.cards.map((card) => `${card.id}:${card.priority}:${card.span ?? ''}`),
	].join('|');

export const isSensorReportStale = (
	updatedUnixSeconds: number | null | undefined,
	now = Date.now(),
): boolean => {
	if (updatedUnixSeconds == null || Number.isNaN(updatedUnixSeconds)) return true;
	return now - updatedUnixSeconds * 1000 > SENSOR_NO_REPORTS_MS;
};

export const inferSensorOverlay = ({
	isBle,
	online,
	uiStateType,
	hasReporter,
	noRecentReports,
}: Pick<
	SensorPriorityLayoutInput,
	'isBle' | 'online' | 'uiStateType' | 'hasReporter' | 'noRecentReports'
>): SensorOverlay | null => {
	if (isBle) {
		if (!hasReporter) return 'noGateway';
		if (noRecentReports) return 'noReports';
		return null;
	}
	if (!online || uiStateType === SENSOR_UI_OFFLINE) return 'offline';
	return null;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const asNumber = (value: unknown): number | null =>
	typeof value === 'number' && Number.isFinite(value) ? value : null;

const asBoolean = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);

const asString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

const isValidReading = (value: number | null): value is number =>
	value != null && !SENSOR_INVALID_READING.has(value);

const channelOf = (input: SensorPriorityLayoutInput): number => input.channel ?? 0;

const componentAt = (
	status: Record<string, unknown> | undefined,
	name: string,
	channel: number,
): Record<string, unknown> | undefined =>
	asRecord(status?.[`${name}:${channel}`]) ?? asRecord(status?.[`${name}:0`]);

const formatMetric = (value: number, digits: number): string => {
	const rounded = Number(value.toFixed(digits));
	return Number.isInteger(rounded) && digits === 0 ? String(rounded) : String(rounded);
};

const batteryIcon = (percent: number): string => {
	if (percent <= SENSOR_CRITICAL_BATTERY_PERCENT) return 'fa-solid fa-battery-empty';
	if (percent <= 25) return 'fa-duotone fa-light fa-battery-quarter';
	return 'fa-duotone fa-light fa-battery-full';
};

const batteryTone = (percent: number): SensorPriorityCardTone | undefined => {
	if (percent < SENSOR_CRITICAL_BATTERY_PERCENT) return 'danger';
	if (percent < SENSOR_LOW_BATTERY_PERCENT) return 'warning';
	return undefined;
};

const temperatureUnit = (input: SensorPriorityLayoutInput): 'C' | 'F' => {
	const status = input.status;
	const tmp = asRecord(status?.tmp);
	const fromStatus = asString(tmp?.units);
	if (fromStatus === 'F' || fromStatus === 'C') return fromStatus;
	const unit = input.temperatureUnit;
	if (unit === 'F' || unit === 'C') return unit;
	return 'C';
};

const toCelsius = (value: number, unit: 'C' | 'F'): number =>
	unit === 'F' ? ((value - 32) * 5) / 9 : value;

const climateForCelsius = (tC: number): { accentColor?: string; promote?: boolean } => {
	if (tC < SENSOR_TEMP_COLD_C) {
		return { accentColor: SENSOR_CLIMATE_COOL, promote: tC < SENSOR_TEMP_COLD_PROMOTE_C };
	}
	if (tC > SENSOR_TEMP_WARM_C) {
		return { accentColor: SENSOR_CLIMATE_WARM, promote: tC > SENSOR_TEMP_WARM_PROMOTE_C };
	}
	return { accentColor: SENSOR_CLIMATE_NEUTRAL };
};

const extractTemperature = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const unit = temperatureUnit(input);
	const digits = unit === 'F' ? 0 : 1;
	const gen1 = asRecord(input.status?.tmp);
	const gen1Value = asNumber(gen1?.value);
	if (isValidReading(gen1Value)) {
		const climate = climateForCelsius(toCelsius(gen1Value, unit));
		return {
			id: 'temperature',
			value: formatMetric(gen1Value, digits),
			unit: unit === 'F' ? '°F' : '°C',
			numericValue: gen1Value,
			label: { key: 'title_chart_temperature', default: 'Temperature' },
			icon: 'fa-solid fa-thermometer-three-quarters',
			kind: 'metric',
			...climate,
		};
	}

	const component = componentAt(input.status, 'temperature', channel);
	const tC = asNumber(component?.tC);
	const value = unit === 'F' ? asNumber(component?.tF) : tC;
	if (!isValidReading(value)) return null;
	const climate = climateForCelsius(tC ?? toCelsius(value, unit));
	return {
		id: 'temperature',
		value: formatMetric(value, digits),
		unit: unit === 'F' ? '°F' : '°C',
		numericValue: value,
		label: { key: 'title_chart_temperature', default: 'Temperature' },
		icon: 'fa-solid fa-thermometer-three-quarters',
		kind: 'metric',
		...climate,
	};
};

const extractHumidity = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const gen1 = asNumber(asRecord(input.status?.hum)?.value);
	if (isValidReading(gen1)) {
		return {
			id: 'humidity',
			value: formatMetric(gen1, 0),
			unit: '%',
			numericValue: gen1,
			label: { key: 'title_chart_humidity', default: 'Humidity' },
			icon: 'fa-solid fa-humidity',
			kind: 'metric',
			accentColor: SENSOR_CLIMATE_HUMIDITY,
		};
	}

	const rh = asNumber(componentAt(input.status, 'humidity', channel)?.rh);
	if (!isValidReading(rh)) return null;
	return {
		id: 'humidity',
		value: formatMetric(rh, 0),
		unit: '%',
		numericValue: rh,
		label: { key: 'title_chart_humidity', default: 'Humidity' },
		icon: 'fa-solid fa-humidity',
		kind: 'metric',
		accentColor: SENSOR_CLIMATE_HUMIDITY,
	};
};

const extractPower = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const status = input.status;
	const settings = input.settings;
	const gen1Bat = asNumber(asRecord(status?.bat)?.value);
	const externalPower = settings?.external_power === 1 || settings?.external_power === true;
	const charger = Boolean(status?.charger);
	const devicePower =
		componentAt(status, 'devicepower', channel) ?? asRecord(status?.['devicepower:0']);
	const battery = asRecord(devicePower?.battery);
	const percent = asNumber(battery?.percent);
	const usb =
		charger || externalPower || gen1Bat === -1 || asRecord(devicePower?.external)?.present === true;

	if (usb) {
		return {
			id: 'battery',
			value: 'Power',
			valueLabel: { key: 'ext_power_hnt', default: 'power' },
			label: { key: 'collapse_title_battery', default: 'Battery' },
			icon: 'fa-solid fa-plug',
			kind: 'status',
		};
	}

	const resolvedPercent = isValidReading(percent) ? percent : isValidReading(gen1Bat) ? gen1Bat : null;
	if (resolvedPercent == null) return null;

	const tone = batteryTone(resolvedPercent);
	return {
		id: 'battery',
		value: formatMetric(resolvedPercent, 0),
		unit: '%',
		numericValue: resolvedPercent,
		label: { key: 'collapse_title_battery', default: 'Battery' },
		icon: batteryIcon(resolvedPercent),
		kind: 'metric',
		tone,
		promote: resolvedPercent < SENSOR_LOW_BATTERY_PERCENT,
	};
};

const illuminationOf = (value: unknown): 'dark' | 'twilight' | 'bright' | undefined => {
	if (value === 'dark' || value === 'twilight' || value === 'bright') return value;
	return undefined;
};

const classifyLux = (
	lux: number | null,
	settings: Record<string, unknown> | undefined,
): 'dark' | 'twilight' | 'bright' | undefined => {
	if (lux == null) return undefined;
	const dark = asNumber(settings?.dark_threshold);
	const dusk = asNumber(settings?.twilight_threshold);
	if (dark == null || dusk == null || dusk <= 0) return undefined;
	if (lux <= dark) return 'dark';
	if (lux < dusk) return 'twilight';
	return 'bright';
};

const extractLux = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const status = input.status;
	const illuminance = componentAt(status, 'illuminance', channel) ?? asRecord(status?.lux);
	const lux =
		asNumber(illuminance?.lux) ??
		asNumber(asRecord(status?.lux)?.value) ??
		asNumber(illuminance?.value);
	const illumination =
		illuminationOf(illuminance?.illumination) ??
		classifyLux(isValidReading(lux) ? lux : null, input.settings);
	const isDark = illumination === 'dark';

	if (isValidReading(lux)) {
		return {
			id: 'lux',
			value: formatMetric(lux, 0),
			unit: 'lx',
			numericValue: lux,
			label: { key: 'label_illuminance', default: 'Illuminance' },
			icon: 'fa-solid fa-sun',
			kind: 'metric',
			tone: isDark ? 'warning' : undefined,
		};
	}

	if (!illumination) return null;
	const illuminationLabel =
		illumination === 'dark'
			? { key: 'alarm_state_dark', default: 'dark' }
			: illumination === 'twilight'
				? { key: 'alarm_state_twilight', default: 'twilight' }
				: { key: 'alarm_state_bright', default: 'bright' };
	return {
		id: 'lux',
		value: illuminationLabel.default,
		valueLabel: illuminationLabel,
		label: { key: 'label_illuminance', default: 'Illuminance' },
		icon: 'fa-solid fa-sun',
		kind: 'status',
		tone: isDark ? 'warning' : undefined,
	};
};

const extractDoor = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const status = input.status;
	const window = componentAt(status, 'window', channel);
	const sensor = asRecord(status?.sensor);
	const open = asBoolean(window?.open) ?? asString(sensor?.state) === 'open';
	const hasWindow = Boolean(window) || asString(sensor?.state) != null;
	if (!hasWindow) return null;

	const rotation = asNumber(componentAt(status, 'rot', channel)?.rot);
	const tiltComponent = asNumber(componentAt(status, 'tilt', channel)?.angle);
	const accelTilt = asNumber(asRecord(status?.accel)?.tilt);
	const tilt =
		[rotation, tiltComponent, accelTilt].find((value) => value != null && value > -1) ?? null;
	const tiltDegrees = tilt != null ? Math.round(tilt) : null;
	const tilted = tiltDegrees != null && tiltDegrees >= 1;

	if (tilted) {
		return {
			id: 'door',
			value: formatMetric(tiltDegrees, 0),
			unit: '°',
			numericValue: tiltDegrees,
			label: { key: 'label_sensor_open', default: 'Open' },
			icon: 'fa-solid fa-door-open',
			kind: 'status',
			tone: 'warning',
		};
	}

	if (open) {
		return {
			id: 'door',
			value: 'Open',
			valueLabel: { key: 'label_sensor_open', default: 'Open' },
			label: { key: 'title_door', default: 'Door' },
			icon: 'fa-solid fa-door-open',
			kind: 'status',
			tone: 'warning',
		};
	}

	return {
		id: 'door',
		value: 'Closed',
		valueLabel: { key: 'state_closed', default: 'closed' },
		label: { key: 'title_door', default: 'Door' },
		icon: 'fa-solid fa-door-closed',
		kind: 'status',
	};
};

const extractVibration = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const status = input.status;
	const accel = asRecord(status?.accel);
	const accelValue = asNumber(accel?.vibration);
	const sensorVibration = asBoolean(asRecord(status?.sensor)?.vibration);
	const bleVibration = asBoolean(asRecord(status?.['vibration:0'])?.value);

	let detected: boolean | null = null;
	if (accelValue != null && accelValue > -1) detected = accelValue > 0;
	else if (sensorVibration != null) detected = sensorVibration;
	else if (bleVibration != null) detected = bleVibration;

	if (detected == null) return null;
	if (input.family === 'dw' && !detected) return null;
	return {
		id: 'vibration',
		value: detected ? 'Detected' : 'None',
		valueLabel: detected
			? { key: 'vibration_detected', default: 'vibration detected' }
			: { key: 'no_vibration', default: 'no vibration' },
		label: { key: 'prop_vibration', default: 'Vibration' },
		icon: 'fa-solid fa-waveform',
		kind: 'status',
		tone: detected ? 'warning' : undefined,
		promote: detected,
	};
};

const extractMotion = (input: SensorPriorityLayoutInput): SensorReading | null => {
	const channel = channelOf(input);
	const status = input.status;
	const settings = input.settings;
	const bleMotion = asBoolean(componentAt(status, 'motion', channel)?.motion);
	const sensor = asRecord(status?.sensor);
	const gen1Motion = asBoolean(sensor?.motion);
	const motionEnabled = asRecord(settings?.motion)?.enabled !== false;
	const sensorActive = sensor?.active !== false;

	if (bleMotion == null && gen1Motion == null && !sensor) return null;

	if (sensor && (!motionEnabled || !sensorActive)) {
		return {
			id: 'motion',
			value: 'Disabled',
			valueLabel: { key: 'disabled', default: 'disabled' },
			label: { key: 'label_occupancy', default: 'Occupancy' },
			icon: 'fa-solid fa-eye-slash',
			kind: 'status',
			tone: 'muted',
		};
	}

	const detected = Boolean(bleMotion ?? gen1Motion);
	return {
		id: 'motion',
		value: detected ? 'Motion' : 'Clear',
		valueLabel: detected
			? { key: 'label_sensor_motion_detected', default: 'Motion' }
			: { key: 'label_sensor_motion_clear', default: 'Clear' },
		label: { key: 'label_occupancy', default: 'Occupancy' },
		icon: detected ? 'fa-solid fa-person-walking' : 'fa-solid fa-person',
		kind: 'status',
		tone: detected ? 'warning' : undefined,
	};
};

const collectReadings = (input: SensorPriorityLayoutInput): SensorReading[] => {
	const byId = new Map<string, SensorReading | null>();
	const take = (id: string, reading: SensorReading | null) => {
		if (reading) byId.set(id, reading);
	};

	if (input.family === 'ht') {
		take('temperature', extractTemperature(input));
		take('humidity', extractHumidity(input));
		take('battery', extractPower(input));
		return ['temperature', 'humidity', 'battery']
			.map((id) => byId.get(id))
			.filter((reading): reading is SensorReading => Boolean(reading));
	}

	if (input.family === 'dw') {
		take('door', extractDoor(input));
		take('lux', extractLux(input));
		take('vibration', extractVibration(input));
		take('battery', extractPower(input));
		return ['door', 'lux', 'vibration', 'battery']
			.map((id) => byId.get(id))
			.filter((reading): reading is SensorReading => Boolean(reading));
	}

	take('motion', extractMotion(input));
	take('vibration', extractVibration(input));
	take('lux', extractLux(input));
	take('temperature', extractTemperature(input));
	take('battery', extractPower(input));
	return ['motion', 'vibration', 'lux', 'temperature', 'battery']
		.map((id) => byId.get(id))
		.filter((reading): reading is SensorReading => Boolean(reading));
};

const defaultPrimaryId = (
	family: SensorPriorityFamily,
	readings: SensorReading[],
): string | undefined => {
	if (family === 'ht')
		return readings.find((reading) => reading.id === 'temperature')?.id ?? readings[0]?.id;
	if (family === 'dw') return readings.find((reading) => reading.id === 'door')?.id ?? readings[0]?.id;
	return readings.find((reading) => reading.id === 'motion')?.id ?? readings[0]?.id;
};

const defaultSecondaryIds = (family: SensorPriorityFamily, primaryId: string | undefined): string[] => {
	if (family === 'ht') return primaryId === 'temperature' ? ['humidity'] : ['temperature'];
	if (family === 'dw') return primaryId === 'door' ? ['lux'] : ['door', 'lux'];
	if (primaryId === 'motion') return ['lux', 'temperature'];
	return ['motion', 'temperature'];
};

const HT_FORCES: SensorPriorityDevForce[] = ['comfortable', 'cold', 'warm', 'humid', 'lowBattery'];
const DW_FORCES: SensorPriorityDevForce[] = ['closed', 'open', 'tilted', 'vibration', 'lowBattery'];
const MOTION_FORCES: SensorPriorityDevForce[] = ['clear', 'detected', 'dark', 'lowBattery'];

const overlayForcesFor = (isBle: boolean): SensorPriorityDevForce[] =>
	isBle ? ['noReports', 'noGateway'] : ['offline'];

const familyForces = (family: SensorPriorityFamily): SensorPriorityDevForce[] => {
	if (family === 'ht') return HT_FORCES;
	if (family === 'dw') return DW_FORCES;
	return MOTION_FORCES;
};

export const isSensorPriorityDevForceForFamily = (
	family: SensorPriorityFamily,
	force: SensorPriorityDevForce,
	isBle: boolean,
): boolean => {
	if (force === 'auto') return true;
	return familyForces(family).includes(force) || overlayForcesFor(isBle).includes(force);
};

export const sensorPriorityDevOptionsFor = (
	family: SensorPriorityFamily,
	isBle: boolean,
): SensorPriorityDevOption[] => {
	const labels: Record<SensorPriorityDevForce, string> = {
		auto: 'Auto',
		comfortable: 'Comfortable',
		cold: 'Too cold',
		warm: 'Too warm',
		humid: 'Humid',
		closed: 'Closed',
		open: 'Open',
		tilted: 'Tilted',
		vibration: 'Vibration',
		clear: 'Clear',
		detected: 'Motion',
		dark: 'Dark',
		lowBattery: 'Low battery',
		noReports: 'No reports',
		noGateway: 'No gateway',
		offline: 'Offline',
	};
	return ['auto', ...familyForces(family), ...overlayForcesFor(isBle)].map((id) => ({
		id,
		label: labels[id],
	}));
};

const componentStatus = (
	channel: number,
	name: string,
	value: Record<string, unknown>,
): Record<string, unknown> => ({ [`${name}:${channel}`]: value });

const fixtureStatus = (
	family: SensorPriorityFamily,
	force: SensorPriorityDevForce,
	channel: number,
): Record<string, unknown> => {
	const power = (percent: number) => componentStatus(channel, 'devicepower', { battery: { percent } });
	const climate = (tC: number, rh: number, percent: number) => ({
		...componentStatus(channel, 'temperature', {
			tC,
			tF: Number((tC * 9) / 5 + 32),
		}),
		...componentStatus(channel, 'humidity', { rh }),
		...power(percent),
	});
	const opening = (open: boolean, angle: number, lux: number, percent: number, vibration = false) => ({
		...componentStatus(channel, 'window', { open }),
		...componentStatus(channel, 'tilt', { angle }),
		...componentStatus(channel, 'illuminance', { lux }),
		...componentStatus(channel, 'vibration', { value: vibration }),
		...power(percent),
	});
	const occupancy = (
		motion: boolean,
		lux: number,
		illumination: 'dark' | 'bright',
		tC: number,
		percent: number,
	) => ({
		...componentStatus(channel, 'motion', { motion }),
		...componentStatus(channel, 'illuminance', { lux, illumination }),
		...componentStatus(channel, 'temperature', { tC }),
		...power(percent),
	});

	if (family === 'ht') {
		if (force === 'cold') return climate(16, 48, 86);
		if (force === 'warm') return climate(28.6, 41, 86);
		if (force === 'humid') return climate(23.1, 74, 86);
		if (force === 'lowBattery') return climate(22.4, 47, 8);
		return climate(22.4, 48, 86);
	}

	if (family === 'dw') {
		if (force === 'open') return opening(true, 0, 420, 78);
		if (force === 'tilted') return opening(true, 86, 420, 78);
		if (force === 'vibration') return opening(false, 0, 124, 78, true);
		if (force === 'lowBattery') return opening(false, 0, 124, 9);
		return opening(false, 0, 124, 78);
	}

	if (force === 'detected') return occupancy(true, 64, 'bright', 21.8, 64);
	if (force === 'dark') return occupancy(false, 6, 'dark', 21.8, 64);
	if (force === 'lowBattery') return occupancy(false, 86, 'bright', 21.8, 11);
	return occupancy(false, 86, 'bright', 21.8, 64);
};

export const applyForcedSensorInput = (
	input: SensorPriorityLayoutInput,
	force: SensorPriorityDevForce | undefined,
): SensorPriorityLayoutInput => {
	if (
		!force ||
		force === 'auto' ||
		!isSensorPriorityDevForceForFamily(input.family, force, input.isBle)
	) {
		return input;
	}

	const status = {
		...input.status,
		...fixtureStatus(input.family, force, channelOf(input)),
	};

	if (force === 'noGateway') {
		return {
			...input,
			status,
			hasReporter: false,
			noRecentReports: false,
			online: true,
		};
	}
	if (force === 'noReports') {
		return {
			...input,
			status,
			hasReporter: true,
			noRecentReports: true,
			online: true,
		};
	}
	if (force === 'offline') {
		return {
			...input,
			status,
			online: false,
			uiStateType: SENSOR_UI_OFFLINE,
		};
	}

	return {
		...input,
		status,
		hasReporter: input.isBle ? true : input.hasReporter,
		noRecentReports: false,
		online: true,
		uiStateType: undefined,
	};
};

const applyForcedReadingFlags = (
	readings: SensorReading[],
	force: SensorPriorityDevForce | undefined,
): SensorReading[] => {
	if (force === 'humid') {
		return readings.map((reading) =>
			reading.id === 'humidity' ? { ...reading, promote: true } : reading,
		);
	}
	return readings;
};

const SENSOR_PRIORITY_SPAN: Record<SensorPriorityCardPriority, number> = {
	primary: 6,
	wide: 6,
	secondary: 3,
	tertiary: 3,
	compact: 2,
};

/** Lone half-width cards take the row. Cards never reorder. */
export const packSensorPrioritySpans = (cards: SensorPriorityCard[]): SensorPriorityCard[] => {
	if (!cards.length) return cards;
	const columns = 6;
	const rows: number[][] = [];
	let row: number[] = [];
	let col = 0;

	cards.forEach((card, index) => {
		const span = SENSOR_PRIORITY_SPAN[card.priority];
		if (col > 0 && col + span > columns) {
			rows.push(row);
			row = [];
			col = 0;
		}
		row.push(index);
		col += span;
		if (col >= columns) {
			rows.push(row);
			row = [];
			col = 0;
		}
	});
	if (row.length) rows.push(row);

	const lone = new Set<number>();
	for (const indices of rows) {
		const total = indices.reduce(
			(sum, index) => sum + SENSOR_PRIORITY_SPAN[cards[index].priority],
			0,
		);
		if (indices.length === 1 && total < columns) lone.add(indices[0]);
	}
	if (!lone.size) return cards;
	return cards.map((card, index) => (lone.has(index) ? { ...card, span: 'full' } : card));
};

export const buildSensorPriorityLayout = (input: SensorPriorityLayoutInput): SensorPriorityLayout => {
	const force = input.force ?? 'auto';
	const forcedInput = applyForcedSensorInput(input, force);
	const overlay = inferSensorOverlay(forcedInput);
	const readings = applyForcedReadingFlags(collectReadings(forcedInput), force);
	const battery = readings.find((reading) => reading.id === 'battery');
	const lowBattery = Boolean(battery?.promote);
	const promoted = readings.find(
		(reading) => reading.promote && reading.id !== 'battery' && reading.id !== 'lux',
	);

	let primaryId = defaultPrimaryId(forcedInput.family, readings);
	if (overlay) {
		primaryId = undefined;
	} else if (lowBattery) {
		primaryId = 'battery';
	} else if (promoted) {
		primaryId = promoted.id;
	}
	if (primaryId === 'lux') {
		primaryId = readings.find((reading) => reading.id !== 'lux')?.id;
	}

	const secondaryIds = new Set(
		overlay
			? [defaultPrimaryId(forcedInput.family, readings)].filter((id): id is string => Boolean(id))
			: defaultSecondaryIds(forcedInput.family, primaryId),
	);
	if (primaryId) secondaryIds.delete(primaryId);

	const cards = packSensorPrioritySpans(
		readings.map((reading): SensorPriorityCard => {
			const { promote: _, ...rest } = reading;
			let priority: SensorPriorityCardPriority = 'tertiary';
			if (rest.id === 'lux') {
				priority = secondaryIds.has(rest.id) ? 'secondary' : 'tertiary';
			} else if (rest.id === primaryId) priority = rest.id === 'battery' ? 'wide' : 'primary';
			else if (secondaryIds.has(rest.id)) priority = 'secondary';
			return {
				...rest,
				priority,
				stale: Boolean(overlay),
				disabled: Boolean(overlay),
			};
		}),
	);

	return {
		family: forcedInput.family,
		overlay,
		cards,
	};
};

/** isMotionIcon prefixes walking/open with `text-primary`; PropertyRow uses tone instead. */
export function stripSensorEventIconTint(icon: string): string {
	if (typeof icon !== 'string') return '';
	return icon
		.replace(/\btext-primary\b/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

export function sensorEventRowTone(icon: string): SensorPriorityCardTone | undefined {
	const raw = stripSensorEventIconTint(icon);
	if (raw.includes('fa-person-walking') || raw.includes('fa-door-open')) return 'warning';
}

export function isSensorOccupancyClearIcon(icon: string): boolean {
	const raw = stripSensorEventIconTint(icon);
	return raw.includes('fa-person') && !raw.includes('fa-person-walking');
}

export function isSensorLastEventStatusIcon(icon: string): boolean {
	const raw = stripSensorEventIconTint(icon);
	if (!raw) return false;
	return (
		raw.includes('fa-person-walking') ||
		raw.includes('fa-door-open') ||
		raw.includes('fa-door-closed') ||
		isSensorOccupancyClearIcon(raw)
	);
}

type SensorLastEventIconSource = {
	icon?: { icon?: unknown };
	data?: {
		state?: { icon?: unknown; value?: unknown };
		door?: { icon?: unknown; value?: unknown };
	};
};

/** Prefer occupancy / door glyphs over the event-log row icon (often `reason`). */
export function pickSensorLastEventIcon(event: SensorLastEventIconSource): string {
	const asIcon = (value: unknown): string => (typeof value === 'string' ? value : '');
	const fromGlyphs =
		[asIcon(event.icon?.icon), asIcon(event.data?.state?.icon), asIcon(event.data?.door?.icon)].find(
			isSensorLastEventStatusIcon,
		) ?? '';
	if (fromGlyphs) return fromGlyphs;
	return sensorDoorStateIcon(event.data?.state?.value ?? event.data?.door?.value);
}

export function sensorDoorStateIcon(value: unknown): string {
	if (typeof value === 'boolean') return value ? 'fas fa-door-open' : 'fas fa-door-closed';
	const raw = String(value ?? '')
		.trim()
		.toLowerCase();
	if (raw === 'open' || raw === 'opened') return 'fas fa-door-open';
	if (raw === 'close' || raw === 'closed') return 'fas fa-door-closed';
	return '';
}
